import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../auth";
import { Badge, eur, dt } from "../components/common";
import { toast } from "sonner";
import { LogOut, Repeat, FileText, LifeBuoy, Plus, ShieldCheck } from "lucide-react";

const TABS = [
  { key: "subscricoes", label: "Subscricoes", icon: Repeat },
  { key: "faturas", label: "Faturas e recibos", icon: FileText },
  { key: "suporte", label: "Service Desk", icon: LifeBuoy },
];

export default function Portal() {
  const { logout } = useAuth();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("subscricoes");
  const [subs, setSubs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [showTicket, setShowTicket] = useState(false);
  const [nt, setNt] = useState({ assunto: "", descricao: "", prioridade: "media" });
  const [reply, setReply] = useState({});

  const loadAll = async () => {
    try {
      const [m, s, i, t] = await Promise.all([
        api.get("/portal/me"), api.get("/portal/subscriptions"),
        api.get("/portal/invoices"), api.get("/portal/tickets"),
      ]);
      setMe(m.data); setSubs(s.data); setInvoices(i.data); setTickets(t.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  const cancelSub = async (id) => {
    try { await api.post(`/portal/subscriptions/${id}/cancel`); toast.success("Cancelamento agendado."); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const createTicket = async () => {
    if (!nt.assunto.trim()) return toast.error("Indique o assunto.");
    try { await api.post("/portal/tickets", nt); toast.success("Ticket criado."); setShowTicket(false); setNt({ assunto: "", descricao: "", prioridade: "media" }); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendReply = async (id) => {
    const msg = (reply[id] || "").trim();
    if (!msg) return;
    try { await api.post(`/portal/tickets/${id}/reply`, { mensagem: msg }); setReply({ ...reply, [id]: "" }); toast.success("Resposta enviada."); loadAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#F6F9FC]">
      <header className="bg-navy text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <div className="text-blue uppercase text-xs font-semibold tracking-[0.25em]">Portal do Cliente</div>
            <h1 className="text-xl font-bold tracking-tight" data-testid="portal-org">{me?.organizacao_nome || "..."}</h1>
          </div>
          <button onClick={logout} data-testid="portal-logout" className="inline-flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-colors"><LogOut size={16} /> Sair</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6 border-b border-tint">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`portal-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-blue text-navy" : "border-transparent text-muted2 hover:text-navy"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "subscricoes" && (
          <div className="space-y-3" data-testid="portal-subscriptions">
            {subs.length === 0 && <p className="text-muted2 text-sm">Sem subscricoes.</p>}
            {subs.map((s) => (
              <div key={s.id} className="bg-white rounded-xl border border-tint p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-navy">{s.plano_nome || s.plataforma || "Subscricao"}</div>
                  <div className="text-xs text-muted2 mt-1">MRR {eur(s.mrr || 0)} - renova {dt(s.periodo_atual_fim)}</div>
                  {s.pagamento_recorrente && <span className="text-[11px] text-green-600">Renovacao automatica ativa</span>}
                </div>
                <div className="flex items-center gap-3">
                  <Badge value={s.estado} />
                  {me?.permitir_cancelamento_recorrente && s.pagamento_recorrente && s.estado === "ativa" && (
                    <button onClick={() => cancelSub(s.id)} data-testid={`portal-cancel-${s.id}`} className="text-xs px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors">Cancelar renovacao</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "faturas" && (
          <div className="space-y-3" data-testid="portal-invoices">
            {invoices.length === 0 && <p className="text-muted2 text-sm">Sem faturas.</p>}
            {invoices.map((f) => (
              <div key={f.id} className="bg-white rounded-xl border border-tint p-4">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-navy">{f.moloni_document_id || "Fatura"} - {eur(f.total ?? f.valor_total)}</div>
                  <Badge value={f.estado} />
                </div>
                {f.mb_entidade && f.mb_referencia && (
                  <div className="text-xs text-muted2 mt-2 inline-flex gap-3 bg-tint rounded p-2">
                    <span>Ent. <b>{f.mb_entidade}</b></span><span>Ref. <b>{f.mb_referencia}</b></span>{f.mb_valor != null && <span>{eur(f.mb_valor)}</span>}
                  </div>
                )}
                <div className="flex gap-4 mt-2 text-xs">
                  {f.moloni_pdf_url && <a href={f.moloni_pdf_url} target="_blank" rel="noreferrer" className="text-blue hover:underline" data-testid={`portal-fatura-pdf-${f.id}`}>Fatura PDF</a>}
                  {f.recibo_pdf_url && <a href={f.recibo_pdf_url} target="_blank" rel="noreferrer" className="text-green-600 hover:underline" data-testid={`portal-recibo-pdf-${f.id}`}>Recibo PDF</a>}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "suporte" && (
          <div data-testid="portal-tickets">
            <div className="flex justify-end mb-4">
              <button onClick={() => setShowTicket(true)} data-testid="portal-new-ticket" className="inline-flex items-center gap-2 bg-blue text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#5BB8E8] transition-colors"><Plus size={16} /> Novo pedido</button>
            </div>
            <div className="space-y-3">
              {tickets.length === 0 && <p className="text-muted2 text-sm">Sem pedidos de suporte.</p>}
              {tickets.map((t) => (
                <div key={t.id} className="bg-white rounded-xl border border-tint p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-navy">{t.assunto}</div>
                    <Badge value={t.estado} />
                  </div>
                  <div className="mt-3 space-y-2">
                    {(t.mensagens || []).map((m, idx) => (
                      <div key={idx} className={`text-sm p-2 rounded-lg ${m.tipo === "cliente" ? "bg-tint" : "bg-blue/10"}`}>
                        <span className="text-[11px] text-muted2">{m.autor} - {dt(m.em)}</span>
                        <div>{m.mensagem}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <input value={reply[t.id] || ""} onChange={(e) => setReply({ ...reply, [t.id]: e.target.value })} data-testid={`portal-reply-input-${t.id}`} placeholder="Escreva uma resposta..." className="flex-1 px-3 py-2 border border-tint rounded-lg text-sm" />
                    <button onClick={() => sendReply(t.id)} data-testid={`portal-reply-send-${t.id}`} className="px-3 py-2 bg-navy text-white rounded-lg text-sm hover:bg-blue transition-colors">Enviar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showTicket && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="portal-ticket-modal">
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-3">
            <h2 className="text-navy font-bold text-lg">Novo pedido de suporte</h2>
            <input value={nt.assunto} onChange={(e) => setNt({ ...nt, assunto: e.target.value })} data-testid="portal-ticket-assunto" placeholder="Assunto" className="w-full px-3 py-2 border border-tint rounded-lg" />
            <textarea value={nt.descricao} onChange={(e) => setNt({ ...nt, descricao: e.target.value })} data-testid="portal-ticket-descricao" placeholder="Descreva o pedido" rows={4} className="w-full px-3 py-2 border border-tint rounded-lg" />
            <select value={nt.prioridade} onChange={(e) => setNt({ ...nt, prioridade: e.target.value })} data-testid="portal-ticket-prioridade" className="w-full px-3 py-2 border border-tint rounded-lg bg-white">
              <option value="baixa">Baixa</option><option value="media">Media</option><option value="alta">Alta</option>
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowTicket(false)} className="px-4 py-2 text-sm text-muted2">Cancelar</button>
              <button onClick={createTicket} data-testid="portal-ticket-save" className="px-4 py-2 bg-blue text-white rounded-lg text-sm font-semibold">Criar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
